import { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button, ControlledTabs, Input, Label, Modal } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useStoreOAuthManual } from '@/hooks/useIdentityCredentials';
import type { OAuthManualStoreRequest, ProviderSummary } from '@/types/identity';
import type { Scope } from './scope';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  provider: ProviderSummary;
  scope: Scope;
}

type Mode = 'fields' | 'json';

interface ParsedBlob {
  serverUrl?: string;
  clientId: string;
  refreshToken: string;
  providerInstanceId?: string;
}

type ParseResult =
  | { ok: true; value: ParsedBlob }
  | { ok: false; error: string };

/**
 * Pull just the wire fields out of the CLI blob. Identity ignores
 * accessToken / expiresAt / expiresAtIso and 400s on confidentialClient:true,
 * so we reject that case here instead of letting it round-trip. `provider`
 * always comes from the picker click context; `serverUrl` and
 * `providerInstanceId` are taken from the catalog's linked MCP server when
 * present, otherwise from the blob.
 */
function parseBlob(raw: string, serverUrlRequired: boolean): ParseResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, error: 'Paste the JSON blob from your local CLI.' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: 'Not valid JSON. Paste the full blob from your local tool.' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Expected a JSON object.' };
  }
  const obj = parsed as Record<string, unknown>;

  if (obj.confidentialClient === true) {
    return {
      ok: false,
      error:
        'This blob is for a confidential OAuth client, which is not supported. Re-run the local flow as a public client.',
    };
  }

  const required: string[] = ['clientId', 'refreshToken'];
  if (serverUrlRequired) required.unshift('serverUrl');
  for (const k of required) {
    if (typeof obj[k] !== 'string' || !(obj[k] as string).trim()) {
      return { ok: false, error: `Missing or empty "${k}" in the pasted blob.` };
    }
  }

  return {
    ok: true,
    value: {
      serverUrl:
        typeof obj.serverUrl === 'string' && obj.serverUrl.trim()
          ? (obj.serverUrl as string).trim()
          : undefined,
      clientId: (obj.clientId as string).trim(),
      refreshToken: (obj.refreshToken as string).trim(),
      providerInstanceId:
        typeof obj.providerInstanceId === 'string' && obj.providerInstanceId.trim()
          ? obj.providerInstanceId.trim()
          : undefined,
    },
  };
}

function errorStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status?: unknown }).status;
    return typeof s === 'number' ? s : undefined;
  }
  return undefined;
}

export default function ConnectOAuthTokenModal({
  isOpen,
  onClose,
  provider,
  scope,
}: Props) {
  const { success } = useToast();
  const store = useStoreOAuthManual();

  // Server context from the catalog. When present, we already know the URL —
  // the user only enters clientId + refreshToken. A provider-wide row carries
  // a flat `mcpServerUrl` (no instance); an instance-bound row carries a full
  // `mcpServer` object with its `serverId`.
  const linkedServerUrl =
    provider.mcpServer?.serverUrl ?? provider.mcpServerUrl ?? null;
  const linkedProviderInstanceId =
    provider.providerInstanceId ?? provider.mcpServer?.serverId ?? null;
  const isLinked = linkedServerUrl !== null;

  const [mode, setMode] = useState<Mode>('fields');
  const [serverUrl, setServerUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [blobText, setBlobText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Where to store the credential. On a Space tab, the user can pick between
  // their own personal scope and the space — the auth fallback chain (space →
  // personal → org-SA) means a personal credential bound to a space-scoped MCP
  // server still resolves correctly. Personal / Org tabs have only one
  // possible destination, so the chooser is hidden there.
  const [storeAt, setStoreAt] = useState<'user' | 'space' | 'org'>(scope.kind);

  const parsedBlob = useMemo(
    () => (mode === 'json' ? parseBlob(blobText, !isLinked) : null),
    [mode, blobText, isLinked],
  );

  const fieldsReady =
    (isLinked || serverUrl.trim().length > 0) &&
    clientId.trim().length > 0 &&
    refreshToken.trim().length > 0;

  const canSubmit = mode === 'fields' ? fieldsReady : parsedBlob?.ok === true;

  const reset = () => {
    setMode('fields');
    setServerUrl('');
    setClientId('');
    setRefreshToken('');
    setBlobText('');
    setDisplayName('');
    setSubmitError(null);
    setStoreAt(scope.kind);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    let payload: ParsedBlob;
    if (mode === 'fields') {
      if (!fieldsReady) return;
      payload = {
        serverUrl: isLinked ? undefined : serverUrl.trim(),
        clientId: clientId.trim(),
        refreshToken: refreshToken.trim(),
      };
    } else {
      if (!parsedBlob?.ok) return;
      payload = parsedBlob.value;
    }

    // Catalog server context wins over anything in the blob — the MCP server
    // already resolved this for us; ignore divergent fields silently.
    const resolvedServerUrl = linkedServerUrl ?? payload.serverUrl;
    const resolvedProviderInstanceId =
      linkedProviderInstanceId ?? payload.providerInstanceId;

    if (!resolvedServerUrl) {
      setSubmitError('Server URL is required.');
      return;
    }

    const body: OAuthManualStoreRequest = {
      provider: provider.id,
      serverUrl: resolvedServerUrl,
      clientId: payload.clientId,
      refreshToken: payload.refreshToken,
      // Personal connects are provider-wide — never bind them to an instance.
      providerInstanceId:
        storeAt === 'user' ? undefined : (resolvedProviderInstanceId ?? undefined),
      displayName: displayName.trim() || undefined,
    };

    try {
      if (storeAt === 'org') {
        await store.mutateAsync({ scope: 'org', body });
      } else if (storeAt === 'user') {
        await store.mutateAsync({ scope: 'user', body });
      } else if (scope.kind === 'space') {
        await store.mutateAsync({ scope: { spaceId: scope.spaceId }, body });
      } else {
        // Defensive: storeAt === 'space' should only be reachable when scope
        // is a space scope (the chooser is hidden otherwise).
        setSubmitError('No space selected for this credential.');
        return;
      }
      success(`${provider.displayName} connected`);
      handleClose();
    } catch (e) {
      const status = errorStatus(e);
      if (status === 422) {
        setSubmitError(
          "That refresh token didn't work — it may have already been used or expired. Re-run the local flow and paste a fresh value.",
        );
        return;
      }
      setSubmitError(e instanceof Error ? e.message : 'Could not store token');
    }
  };

  const blobError =
    mode === 'json' && parsedBlob && !parsedBlob.ok && blobText.trim().length > 0
      ? parsedBlob.error
      : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Connect ${provider.displayName} with OAuth tokens`}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={store.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} isLoading={store.isPending}>
            Connect
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {isLinked
            ? `Provide the OAuth client ID and refresh token for ${provider.displayName}. The server URL comes from the linked MCP server.`
            : `Provide the server URL, OAuth client ID, and refresh token for ${provider.displayName}. Enter the values directly, or paste the JSON blob your local tool produced.`}
        </p>

        {isLinked && provider.mcpServer ? (
          <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1">
            <p className="text-xs text-muted-foreground">Linked MCP server</p>
            <p className="text-sm font-medium text-foreground">
              {provider.mcpServer.name}
            </p>
            <code className="block text-xs font-mono text-muted-foreground break-all">
              {provider.mcpServer.serverUrl}
            </code>
          </div>
        ) : (
          isLinked &&
          linkedServerUrl && (
            <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1">
              <p className="text-xs text-muted-foreground">Server URL</p>
              <code className="block text-xs font-mono text-muted-foreground break-all">
                {linkedServerUrl}
              </code>
            </div>
          )
        )}

        {scope.kind === 'space' && (
          <div className="space-y-1.5">
            <Label>Store credential for</Label>
            <div className="grid grid-cols-2 gap-2">
              <ScopeOption
                label="Just me"
                description="Only your runs use this token."
                active={storeAt === 'user'}
                onClick={() => setStoreAt('user')}
              />
              <ScopeOption
                label={scope.spaceName}
                description="Everyone in this space shares it."
                active={storeAt === 'space'}
                onClick={() => setStoreAt('space')}
              />
            </div>
          </div>
        )}

        <div className="rounded-md border border-status-warning-fg/40 bg-status-warning-bg/30 p-3 flex gap-3">
          <AlertTriangle className="h-4 w-4 text-status-warning-fg shrink-0 mt-0.5" />
          <div className="text-xs text-foreground">
            <p className="font-medium">
              Submitting may invalidate the source refresh token.
            </p>
            <p className="text-muted-foreground mt-0.5">
              On submit, optr takes sole ownership of this OAuth session and
              rotates the refresh token. If the token came from a local tool,
              you&apos;ll need to re-run that flow before using{' '}
              {provider.displayName} there again.
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="oauth-display-name">Display name (optional)</Label>
          <Input
            id="oauth-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Datadog (prod)"
          />
        </div>

        <ControlledTabs
          tabs={[
            { id: 'fields', label: 'Enter fields' },
            { id: 'json', label: 'Paste JSON' },
          ]}
          activeTab={mode}
          onChange={(id) => setMode(id as Mode)}
        >
          {mode === 'fields' ? (
            <div className="space-y-3">
              {!isLinked && (
                <div className="space-y-1.5">
                  <Label htmlFor="oauth-server-url">Server URL</Label>
                  <Input
                    id="oauth-server-url"
                    type="url"
                    value={serverUrl}
                    onChange={(e) => setServerUrl(e.target.value)}
                    placeholder="https://mcp.example.com/api/mcp"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <p className="text-xs text-muted-foreground">
                    The OAuth-protected resource. Used by optr to discover the
                    token endpoint.
                  </p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="oauth-client-id">Client ID</Label>
                <Input
                  id="oauth-client-id"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="OAuth client identifier"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="oauth-refresh-token">Refresh token</Label>
                <Input
                  id="oauth-refresh-token"
                  type="password"
                  value={refreshToken}
                  onChange={(e) => setRefreshToken(e.target.value)}
                  placeholder="OAuth refresh token"
                  autoComplete="new-password"
                  spellCheck={false}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="oauth-blob">Token blob (JSON)</Label>
              <textarea
                id="oauth-blob"
                value={blobText}
                onChange={(e) => setBlobText(e.target.value)}
                placeholder={
                  isLinked
                    ? '{\n  "clientId": "...",\n  "refreshToken": "..."\n}'
                    : '{\n  "clientId": "...",\n  "serverUrl": "...",\n  "refreshToken": "..."\n}'
                }
                rows={10}
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              />
              {isLinked && (
                <p className="text-xs text-muted-foreground">
                  We only read <code>clientId</code> and <code>refreshToken</code>
                  {' '}from the blob — the server URL comes from the linked MCP
                  server.
                </p>
              )}
              {blobError && (
                <p className="text-xs text-status-error-fg" role="alert">
                  {blobError}
                </p>
              )}
            </div>
          )}
        </ControlledTabs>

        {submitError && (
          <p className="text-sm text-status-error-fg" role="alert">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
}

interface ScopeOptionProps {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}

function ScopeOption({ label, description, active, onClick }: ScopeOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-left rounded-md border p-3 transition-colors ${
        active
          ? 'border-primary-500 bg-accent/40'
          : 'border-border bg-card hover:border-primary-500/60 hover:bg-accent/20'
      }`}
    >
      <p className="text-sm font-medium text-foreground truncate">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </button>
  );
}
